import TrackedButton from 'src/designSystem/TrackedButton';
import { twMerge } from 'src/utils/twMerge';
import { ButtonHTMLAttributes, ReactNode, FunctionComponent } from 'react';
import styles from './BumpyCard.module.scss';
import Spinner, { SpinnerType } from 'src/designSystem/Spinner';
import { ImpactStyle, hapticImpact } from 'src/utils/haptics';

type Props = Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'type'> & {
  'data-testid'?: string;
  type?: ButtonType;
  size?: ButtonSize;
  disabled?: boolean;
  loading?: boolean;
  spinnerType?: SpinnerType;
  icon?: ReactNode;
  form?: string;
  htmlType?: ButtonHTMLAttributes<HTMLButtonElement>['type'];
  onClickDisabled?: () => void;
};
export type { Props as ButtonProps };

const commonClasses =
  'rounded-2xl h-auto border-2 border-structural-darkest shadow-structural-darkest flex items-center justify-center gap-2';
const primaryClasses =
  'bg-primary-darkest border-primary-black text-white shadow-primary-black';
const secondaryClasses =
  'bg-structural-lightest text-structural-darkest border-structural-light shadow-structural-light';
const linkClasses =
  'text-structural-darkest underline shadow-none border-none bg-transparent';
const textClasses =
  'bg-none border-none shadow-none text-structural-darkest bg-transparent';
const dangerClasses =
  'bg-red-darkest border-danger-dark shadow-danger-dark text-white shadow-danger-dark';
const successClasses =
  'bg-green-dark border-green-darker shadow-green-darker text-white shadow-green-darker';
const darkClasses =
  'bg-structural-darkest border-structural-darkest shadow-structural-darkest text-white shadow-structural-darkest';
const primaryLightClasses =
  'bg-primary-light border-primary-darkest shadow-primary-darkest text-primary-darkest';
const goldClasses =
  'bg-star-road-gold-button border-warning-neutral shadow-warning-neutral text-structural-darkest';
export const disabledClasses =
  'bg-structural-lighter text-structural-neutral border-transparent shadow-transparent cursor-not-allowed';
const textLoadingClasses = 'text-structural-neutral cursor-not-allowed';

type ButtonType =
  | 'default'
  | 'primary'
  | 'dashed'
  | 'link'
  | 'text'
  | 'danger'
  | 'success'
  | 'dark'
  | 'gold'
  | 'primaryLight';
type ButtonSize = 'small' | 'middle' | 'large';

const getClasses = (
  type?: ButtonType,
  disabled?: boolean,
  loading?: boolean,
  size?: ButtonSize,
) => {
  const isDisabled = disabled || loading;
  return twMerge(
    commonClasses,
    styles.bumpyCardShadow,
    type === 'primary' ? primaryClasses : '',
    type === 'default' ? secondaryClasses : '',
    type === 'link' ? linkClasses : '',
    type === 'text' ? textClasses : '',
    type === 'danger' ? dangerClasses : '',
    type === 'success' ? successClasses : '',
    type === 'dark' ? darkClasses : '',
    type === 'gold' ? goldClasses : '',
    type === 'primaryLight' ? primaryLightClasses : '',
    loading && (type === 'text' || type === 'link')
      ? textLoadingClasses
      : isDisabled
        ? disabledClasses
        : '',
    size === 'small'
      ? 'py-1 w-auto px-2 text-sm font-semibold rounded-lg'
      : size === 'middle'
        ? 'py-3 w-auto px-4 text-base font-bold'
        : 'py-5 px-3 w-full text-base font-bold',
  );
};

const Button: FunctionComponent<Props> = ({
  className,
  onClick,
  onClickDisabled,
  loading,
  spinnerType = 'primary',
  disabled,
  type,
  size,
  icon,
  htmlType,
  children,
  ...restProps
}) => {
  const isDisabled = (disabled || loading) ?? false;

  return (
    <TrackedButton
      className={twMerge(getClasses(type, disabled, loading, size), className)}
      onClick={event => {
        if (onClickDisabled && isDisabled) {
          hapticImpact(ImpactStyle.Light);
          onClickDisabled();
          return;
        }
        if (onClick && !isDisabled) {
          hapticImpact(ImpactStyle.Medium);
          onClick(event);
        }
      }}
      disabled={onClickDisabled ? false : isDisabled}
      aria-busy={loading || undefined}
      type={htmlType}
      {...restProps}
    >
      <div
        className={'relative flex flex-row justify-center items-center gap-2'}
      >
        {loading ? (
          size === 'small' ? (
            <Spinner size={'xxs'} type={spinnerType} />
          ) : (
            <div className={'absolute -left-8'}>
              <Spinner size={'xs'} type={spinnerType} />
            </div>
          )
        ) : icon ? (
          <div>{icon}</div>
        ) : null}
        {children}
      </div>
    </TrackedButton>
  );
};

export default Button;
